import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Input,
  Textarea,
  Select,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Switch,
  Button,
  Flex,
  Text,
  RadioGroup,
  Radio,
  Icon,
  IconButton,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { FiPlus, FiSearch, FiX, FiSave } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { DarwinAgent, DarwinTool, DarwinToolType } from "build-ts/darwin_agent";
import { DarwinAgentType } from "build-ts/common";
import { FaArrowLeft, FaLock, FaReplyAll, FaTasks, FaTools, FaUnlock } from "react-icons/fa";
import { RiRobotFill } from "react-icons/ri";

interface EvaAgentLeftPanelProps {
  agent: DarwinAgent;
  availableTools: DarwinTool[];
  onConfigChange: (field: string, value: any) => void;
  onToolsChange: (selectedToolsArray: DarwinTool[]) => void;
  isEditing?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onSave: () => void;
  isCreateMode?: boolean;
  onViewDocs?: () => void;
}

const EvaAgentLeftPanel: React.FC<EvaAgentLeftPanelProps> = ({
  agent,
  availableTools,
  onConfigChange,
  onToolsChange,
  isEditing = false,
  onEdit,
  onCancel,
  onSave,
  isCreateMode = false,
  onViewDocs,
}) => {
  const navigate = useNavigate();
  const [toolSearchTerm, setToolSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const selectedToolCount = (agent.tools || []).length;

  const handleBackToAgents = () => {
    navigate("/eva/agents");
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      minWidth={0}
      overflowY="auto"
      pr={2}
      css={{
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
      }}
    >
      <VStack spacing={4} align="stretch" flex="1">
        {/* Basic Agent Info */}
        <Box
          bg="white"
          px={4}
          py={2}
          borderRadius="12px"
          boxShadow="lg"
          display="flex"
          flexDirection="column"
          flex="1"
        >
          <Flex justify="space-between" align="center" mb={3}>
            <HStack spacing={2}>
              {!isCreateMode && (
                <Tooltip label={isEditing ? "Cancel" : "Edit agent"}>
                  <IconButton
                    aria-label={isEditing ? "Cancel" : "Edit"}
                    icon={isEditing ? <Icon as={FaUnlock} /> : <Icon as={FaLock} />}
                    size="xs"
                    variant="solid"
                    bg={isEditing ? "purple.700" : "red.100"}
                    color={isEditing ? "white" : "red.700"}
                    _hover={{ bg: isEditing ? "purple.600" : "red.200" }}
                    colorScheme="purple"
                    borderRadius="full"
                    onClick={() => (isEditing ? onCancel?.() : onEdit?.())}
                  />
                </Tooltip>
              )}
              <Text fontSize="md" color="purple.700" fontWeight="700" mb={0}>
                Setup
              </Text>
            </HStack>
            {isCreateMode ? (
              <HStack spacing={2}>
                <Button size="sm" variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button size="sm" colorScheme="green" onClick={onSave} leftIcon={<Icon as={FiPlus} />}>
                  Create
                </Button>
              </HStack>
            ) : !isEditing ? (
              <Button
                aria-label="Back to agents"
                leftIcon={<Icon as={FaArrowLeft} boxSize={4} />}
                size="sm"
                variant="outline"
                onClick={handleBackToAgents}
                borderRadius="full"
              >
                Back
              </Button>
            ) : (
              <HStack spacing={2}>
                <IconButton aria-label="Cancel" icon={<Icon as={FiX} />} size="sm" variant="outline" onClick={onCancel} borderRadius="full" />
                <Button aria-label="Save" leftIcon={<Icon as={FiSave} />} size="sm" colorScheme="green" onClick={onSave} borderRadius="full">
                  Save
                </Button>
              </HStack>
            )}
          </Flex>
          <Tabs index={activeTab} onChange={setActiveTab} variant="line" colorScheme="purple" flex={1} display="flex" flexDirection="column" minH={0}>
            <TabList width="100%">
              <Tab flex={1} justifyContent="center">
                <HStack spacing={2}>
                  <Icon as={RiRobotFill} boxSize={5} />
                  <Text mb={0} fontWeight="700" >Identity</Text>
                </HStack>
              </Tab>
              <Tab flex={1} justifyContent="center">
                <HStack spacing={2}>
                  <Icon as={FaTools} boxSize={4} />
                  <Text mb={0} fontWeight="700" >Tools</Text>
                  {selectedToolCount > 0 && (
                    <Badge colorScheme="purple" borderRadius="full" fontSize="xs">
                      {selectedToolCount}
                    </Badge>
                  )}
                </HStack>
              </Tab>
            </TabList>
            <TabPanels flex={1} overflowY="auto" minH={0}>
              <TabPanel px={0} pt={4} pb={0}>
                <VStack spacing={6} align="stretch">
                  <FormControl>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={agent.header?.name || ""}
                      onChange={(e) => onConfigChange("header.name", e.target.value)}
                      placeholder="Enter agent name"
                      isDisabled={!isEditing && !isCreateMode}
                      color={!isEditing && !isCreateMode ? "gray.800" : "gray.800"}
                      fontSize="sm"
                      fontWeight="600"
                      bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      value={agent.header?.description || ""}
                      onChange={(e) => onConfigChange("header.description", e.target.value)}
                      placeholder="Briefly describe the agent's purpose"
                      rows={3}
                      isDisabled={!isEditing && !isCreateMode}
                      resize="none"
                      fontSize="sm"
                      fontWeight="600"
                      bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                      color={!isEditing && !isCreateMode ? "gray.800" : "gray.800"}
                      _disabled={{ opacity: 1, color: "gray.800" }}
                    />
                  </FormControl>
                  <HStack spacing={4} align="flex-start">
                    <FormControl flex={2}>
                      <FormLabel>Agent Type</FormLabel>
                      {isCreateMode ? (
                        <Select
                          value={agent.header?.type || ""}
                          onChange={(e) => onConfigChange("header.type", e.target.value)}
                          placeholder="Select agent type"
                          fontSize="sm"
                          fontWeight="600"
                          bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                        >
                          {Object.values(DarwinAgentType)
                            .filter(
                              value => typeof value === "string" && value !== "UNRECOGNIZED" && value !== "UNKNOWN_AGENT_TYPE"
                            )
                            .map((agentType) => (
                              <option key={agentType} value={agentType}>
                                {agentType}
                              </option>
                            ))}
                        </Select>
                      ) : (
                        <Input
                          value={agent.header?.type || ""}
                          readOnly
                          color="gray.600"
                          fontSize="sm"
                          fontWeight="600"
                          bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                        />
                      )}
                    </FormControl>
                    <FormControl flex={1}>
                      <FormLabel>Is Active</FormLabel>
                      <Switch
                        isChecked={agent.header?.is_active || false}
                        onChange={(e) => onConfigChange("header.is_active", e.target.checked)}
                        colorScheme="blue"
                        isDisabled={!isEditing && !isCreateMode}
                        mb={0}
                      />
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <HStack mb={2} spacing={1} align="center">
                      <FormLabel mb={0} fontSize="sm" fontWeight="500" color="gray.700">
                        Async Init Message
                      </FormLabel>
                      <Tooltip
                        label="Sent as a separate user message at async chat runtime — not part of the system prompt. Supports the same Jinja variables (e.g. {{ scenario.issue }})."
                        placement="top"
                        hasArrow
                      >
                        <InfoOutlineIcon boxSize={3} color="gray.400" cursor="help" />
                      </Tooltip>
                    </HStack>
                    <Textarea
                      value={(agent as any).context_template ?? ""}
                      onChange={(e) => onConfigChange("context_template", e.target.value)}
                      placeholder="e.g. The user is working on: {{ scenario.issue }}"
                      rows={15}
                      isDisabled={!isEditing && !isCreateMode}
                      resize="none"
                      fontSize="xs"
                      fontFamily="mono"
                      borderColor="gray.200"
                      bg={!isEditing && !isCreateMode ? "gray.50" : "white"}
                      color="gray.800"
                      _hover={{ borderColor: isEditing || isCreateMode ? "purple.300" : "gray.200" }}
                      _focus={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)" }}
                      _disabled={{ opacity: 1, color: "gray.800", cursor: "not-allowed" }}
                    />
                  </FormControl>
                </VStack>
              </TabPanel>
              <TabPanel px={0} pt={4} pb={0}>
                <VStack align="stretch" spacing={3}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FiSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search tools..."
                      value={toolSearchTerm}
                      onChange={(e) => setToolSearchTerm(e.target.value)}
                      bg="white"
                      borderColor="gray.300"
                      _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px purple.400" }}
                    />
                  </InputGroup>
                  {(() => {
                    const searchLower = toolSearchTerm.toLowerCase();
                    const SKILL_TOOL_TYPE = DarwinToolType[DarwinToolType.SKILL_TOOL] as string;

                    const regularTools = availableTools.filter(tool => {
                      const isResponse = !!tool.header?.is_response_tool;
                      const isSkill = tool.header?.type === SKILL_TOOL_TYPE;
                      const matchesSearch =
                        tool.header?.name?.toLowerCase().includes(searchLower) || !searchLower;
                      return !isResponse && !isSkill && matchesSearch;
                    });
                    const responseTools = availableTools.filter(tool =>
                      tool.header?.is_response_tool &&
                      (tool.header?.name?.toLowerCase().includes(searchLower) || !searchLower)
                    );
                    const skillTools = availableTools.filter(tool => {
                      const isSkill = tool.header?.type === SKILL_TOOL_TYPE;
                      const matchesSearch =
                        tool.header?.name?.toLowerCase().includes(searchLower) || !searchLower;
                      return isSkill && matchesSearch;
                    });

                    return (
                      <VStack align="stretch" spacing={3} mt={2}>
                        {/* Task Tools Section */}
                        <Box display="flex" flexDirection="column">
                          <HStack mb={2} align="center" bg="blue.50" p={1} borderRadius="md">
                            <Icon as={FaTasks} boxSize={4} color="blue.600" />
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="blue.600"
                              textAlign="left"
                              mb={0}
                            >
                              Task Completion ({regularTools.length})
                            </Text>
                          </HStack>
                          <Box
                            maxH="240px"
                            overflowY="auto"
                            overflowX="hidden"
                            px={2}
                            css={{
                              "&::-webkit-scrollbar": { width: "4px" },
                              "&::-webkit-scrollbar-track": { background: "transparent" },
                              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
                            }}
                          >
                            {regularTools.length > 0 ? (
                              <Stack spacing={1} width="100%">
                                {regularTools.map((tool) => {
                                  const toolId = tool.header?.id;
                                  const toolName = tool.header?.name;
                                  const isChecked = agent.tools?.some(selectedTool => {
                                    const selectedToolId = selectedTool.header?.id;
                                    return selectedToolId === toolId;
                                  }) || false;

                                  return (
                                    <Checkbox
                                      key={toolId}
                                      id={toolId}
                                      value={toolId}
                                      isChecked={isChecked}
                                      colorScheme="blue"
                                      size="sm"
                                      isDisabled={!isEditing && !isCreateMode}
                                      alignItems="flex-start"
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        const currentTools = (agent.tools || []) as DarwinTool[];
                                        if (isChecked) {
                                          const next = [...currentTools, tool] as DarwinTool[];
                                          onToolsChange(next as any);
                                        } else {
                                          const filtered = currentTools.filter(currentTool => {
                                            const currentToolId = currentTool.header?.id;
                                            return currentToolId !== toolId;
                                          });
                                          onToolsChange(filtered as any);
                                        }
                                      }}
                                    >
                                      <Text
                                        fontSize="sm"
                                        fontWeight="600"
                                        mb={0}
                                        wordBreak="break-word"
                                        whiteSpace="normal"
                                        maxW="100%"
                                        textAlign="left"
                                        lineHeight="1.2"
                                      >
                                        {toolName}
                                      </Text>
                                    </Checkbox>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Text fontSize="xs" color="gray.500">No tools found</Text>
                            )}
                          </Box>
                        </Box>

                        {/* Skill Tools Section */}
                        {skillTools.length > 0 && (
                          <Box
                            display="flex"
                            flexDirection="column"
                            borderTop="1px solid"
                            borderColor="gray.200"
                            pt={2}
                            maxH="240px"
                          >
                            <HStack mb={2} align="center" bg="yellow.50" p={1} borderRadius="md">
                              <Icon as={FaTools} boxSize={4} color="yellow.700" />
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="yellow.700"
                                textAlign="left"
                                mb={0}
                              >
                                Skills ({skillTools.length})
                              </Text>
                            </HStack>
                            <Box
                              maxH="140px"
                              overflowY="auto"
                              overflowX="hidden"
                              px={2}
                              css={{
                                "&::-webkit-scrollbar": { width: "4px" },
                                "&::-webkit-scrollbar-track": { background: "transparent" },
                                "&::-webkit-scrollbar-thumb": {
                                  background: "#CBD5E0",
                                  borderRadius: "2px",
                                },
                              }}
                            >
                              <Stack spacing={1} width="100%">
                                {skillTools.map((tool) => {
                                  const toolId = tool.header?.id;
                                  const toolName = tool.header?.name;
                                  const isChecked =
                                    agent.tools?.some((selectedTool) => {
                                      const selectedToolId = selectedTool.header?.id;
                                      return selectedToolId === toolId;
                                    }) || false;

                                  return (
                                    <Checkbox
                                      key={toolId}
                                      id={toolId}
                                      value={toolId}
                                      isChecked={isChecked}
                                      colorScheme="yellow"
                                      size="sm"
                                      isDisabled={!isEditing && !isCreateMode}
                                      alignItems="flex-start"
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        const currentTools = (agent.tools || []) as DarwinTool[];
                                        if (checked) {
                                          const next = [...currentTools, tool] as DarwinTool[];
                                          onToolsChange(next as any);
                                        } else {
                                          const filtered = currentTools.filter((currentTool) => {
                                            const currentToolId = currentTool.header?.id;
                                            return currentToolId !== toolId;
                                          });
                                          onToolsChange(filtered as any);
                                        }
                                      }}
                                    >
                                      <Text
                                        fontSize="sm"
                                        fontWeight="600"
                                        mb={0}
                                        wordBreak="break-word"
                                        whiteSpace="normal"
                                        maxW="100%"
                                        textAlign="left"
                                        lineHeight="1.2"
                                      >
                                        {toolName}
                                      </Text>
                                    </Checkbox>
                                  );
                                })}
                              </Stack>
                            </Box>
                          </Box>
                        )}

                        {/* Response Tools Section */}
                        {responseTools.length > 0 && (
                          <Box display="flex" flexDirection="column" borderTop="1px solid" borderColor="gray.200" pt={2} maxH="240px">
                            <HStack mb={2} align="center" bg="green.50" p={1} borderRadius="md">
                              <Icon as={FaReplyAll} boxSize={4} color="green.700" />
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color="green.700"
                                textAlign="left"
                                mb={0}
                              >
                                Structured Output ({responseTools.length})
                              </Text>
                            </HStack>
                            <Box
                              maxH="140px"
                              overflowY="auto"
                              overflowX="hidden"
                              px={2}
                              css={{
                                "&::-webkit-scrollbar": { width: "4px" },
                                "&::-webkit-scrollbar-track": { background: "transparent" },
                                "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
                              }}
                            >
                              <RadioGroup
                                value={(() => {
                                  const selectedResponseTool = agent.tools?.find(selectedTool => {
                                    return responseTools.some(responseTool =>
                                      responseTool.header?.id === selectedTool.header?.id
                                    );
                                  });
                                  return selectedResponseTool?.header?.id || "";
                                })()}
                                onChange={(value) => {
                                  const currentTools = (agent.tools || []) as DarwinTool[];
                                  const filteredTools = currentTools.filter(currentTool => {
                                    return !responseTools.some(responseTool =>
                                      responseTool.header?.id === currentTool.header?.id
                                    );
                                  });

                                  if (value) {
                                    const selectedTool = responseTools.find(tool => tool.header?.id === value);
                                    if (selectedTool) {
                                      const next = [...filteredTools, selectedTool] as DarwinTool[];
                                      onToolsChange(next as any);
                                    }
                                  } else {
                                    onToolsChange(filteredTools as any);
                                  }
                                }}
                                isDisabled={!isEditing && !isCreateMode}
                              >
                                <Stack spacing={0} width="100%">
                                  {responseTools.map((tool) => {
                                    const toolId = tool.header?.id;
                                    const toolName = tool.header?.name;
                                    const isSelected = agent.tools?.some(selectedTool => {
                                      return selectedTool.header?.id === toolId;
                                    }) || false;

                                    return (
                                      <Box
                                        key={toolId}
                                        onClick={(e) => {
                                          if (isSelected && (isEditing || isCreateMode)) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            const currentTools = (agent.tools || []) as DarwinTool[];
                                            const filteredTools = currentTools.filter(currentTool => {
                                              return currentTool.header?.id !== toolId;
                                            });
                                            onToolsChange(filteredTools as any);
                                          }
                                        }}
                                        cursor={isSelected && (isEditing || isCreateMode) ? "pointer" : "default"}
                                        textAlign="left"
                                      >
                                        <Radio
                                          value={toolId}
                                          colorScheme="green"
                                          size="sm"
                                          alignItems="center"
                                        >
                                          <Text
                                            fontSize="sm"
                                            fontWeight="600"
                                            mb={0}
                                            wordBreak="break-word"
                                            whiteSpace="normal"
                                            maxW="100%"
                                            textAlign="left"
                                            lineHeight="1.2"
                                          >
                                            {toolName}
                                          </Text>
                                        </Radio>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </RadioGroup>
                            </Box>
                          </Box>
                        )}
                      </VStack>
                    );
                  })()}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>
    </Box>
  );
};

export default EvaAgentLeftPanel;
